import React, { useMemo, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Timer, ArrowRight, Check, Plus, ShoppingBag, Lock } from 'lucide-react';
import { trackAddToCart } from '../lib/pixel';
import { UpsellPopup } from './UpsellPopup';
const BASE_PRICE = 14.99;
const BUMP_1_PRICE = 3.99;
const BUMP_2_PRICE = 4.99;
const BUMPS_TOTAL = BUMP_1_PRICE + BUMP_2_PRICE; // 10.98
const TRUE_TOTAL = +(BASE_PRICE + BUMPS_TOTAL).toFixed(2); // 17.34
const BUNDLE_PRICE = +(BASE_PRICE + BUMPS_TOTAL * 0.7).toFixed(2); // 14.05 (30% off bumps)
const SAVINGS = +(TRUE_TOTAL - BUNDLE_PRICE).toFixed(2); // 3.29
const SHOPIFY_VARIANTS = {
  base: '44208175841316',
  bump1: '44217414516772',
  bump2: '44217423593508',
  both: '44217425788964'
};
// Discounted bundle variant (used by the upsell popup accept button)
const FULL_BUNDLE_URL = 'https://noveliahub.com/cart/44228944658468:1';
const MAIN_ONLY_URL = `https://noveliahub.com/cart/${SHOPIFY_VARIANTS.base}:1`;
const formatPrice = (value: number) => value.toFixed(2);
export function Offer() {
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const total = useMemo(() => {
    let t = BASE_PRICE;
    if (bump1) t += BUMP_1_PRICE;
    if (bump2) t += BUMP_2_PRICE;
    return t;
  }, [bump1, bump2]);
  const checkoutUrl = useMemo(() => {
    let variantId = SHOPIFY_VARIANTS.base;
    if (bump1 && bump2) variantId = SHOPIFY_VARIANTS.both;else if (bump1) variantId = SHOPIFY_VARIANTS.bump1;else if (bump2) variantId = SHOPIFY_VARIANTS.bump2;
    return `https://noveliahub.com/cart/${variantId}:1`;
  }, [bump1, bump2]);
  const handleCheckoutClick = () => {
    const contents = [{
      id: 'master-reposteria',
      quantity: 1,
      item_price: BASE_PRICE
    }];
    const names = ['Master en Repostería en Casa'];
    if (bump1) {
      contents.push({
        id: 'bump-costos',
        quantity: 1,
        item_price: BUMP_1_PRICE
      });
      names.push('Guía de Costos y Precios');
    }
    if (bump2) {
      contents.push({
        id: 'bump-rellenos',
        quantity: 1,
        item_price: BUMP_2_PRICE
      });
      names.push('Pack de 10 Rellenos Gourmet');
    }
    trackAddToCart({
      value: total,
      currency: 'USD',
      contents,
      content_name: names.join(' + ')
    });
  };
  // Intercept checkout click: if user has NO bumps selected, show upsell popup instead of navigating
  const handleCheckoutInterceptor = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!bump1 && !bump2) {
      e.preventDefault();
      setShowUpsell(true);
      return;
    }
    handleCheckoutClick();
  };
  const handleUpsellAccept = () => {
    trackAddToCart({
      value: BUNDLE_PRICE,
      currency: 'USD',
      content_name: 'Bundle Completo (Master + Guía de Costos + Pack de Rellenos)',
      contents: [{
        id: 'master-reposteria',
        quantity: 1,
        item_price: BASE_PRICE
      }, {
        id: 'bump-costos',
        quantity: 1,
        item_price: BUMP_1_PRICE * 0.7
      }, {
        id: 'bump-rellenos',
        quantity: 1,
        item_price: BUMP_2_PRICE * 0.7
      }]
    });
    window.location.href = FULL_BUNDLE_URL;
  };
  const handleUpsellDecline = () => {
    trackAddToCart({
      value: BASE_PRICE,
      currency: 'USD',
      content_name: 'Master en Repostería en Casa',
      contents: [{
        id: 'master-reposteria',
        quantity: 1,
        item_price: BASE_PRICE
      }]
    });
    window.location.href = MAIN_ONLY_URL;
  };
  return <section id="oferta" className="py-24 px-4 sm:px-6 lg:px-8 relative bg-cream" data-id="element-216">
      <div className="dashed-divider absolute top-0 left-0" data-id="element-217"></div>

      <div className="max-w-4xl mx-auto" data-id="element-218">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="bg-white rounded-[2.5rem] shadow-2xl p-8 md:p-12 border-2 border-caramel/20 relative overflow-hidden" data-id="element-219">
          {/* Decorative background */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-rose-light/20 rounded-full blur-3xl -translate-y-3/4 translate-x-3/4 pointer-events-none" data-id="element-220"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-caramel-light/15 rounded-full blur-3xl translate-y-3/4 -translate-x-3/4 pointer-events-none" data-id="element-221"></div>

          <div className="relative z-10 text-center" data-id="element-222">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full font-bold text-sm mb-8 animate-pulse" data-id="element-223">
              <Timer className="w-4 h-4" data-id="element-224" />
              OFERTA DE LANZAMIENTO
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-espresso mb-6 leading-[1.15] pb-2" data-id="element-225">
              Empieza tu negocio dulce hoy
            </h2>

            {/* Dynamic Price */}
            <div className="flex flex-col items-center justify-center mb-8" data-id="element-226">
              <span className="text-2xl text-espresso-light/60 line-through font-medium mb-2" data-id="element-227">
                Precio Real: $29.99 USD
              </span>
              <div className="flex items-start justify-center gap-1 min-h-[100px] md:min-h-[120px]" data-id="element-228">
                <span className="text-3xl font-bold text-caramel mt-2" data-id="element-229">$</span>
                <div className="relative overflow-hidden" data-id="element-230">
                  <AnimatePresence mode="popLayout" data-id="element-231">
                    <motion.span key={total.toFixed(2)} initial={{
                    y: 40,
                    opacity: 0
                  }} animate={{
                    y: 0,
                    opacity: 1
                  }} exit={{
                    y: -40,
                    opacity: 0
                  }} transition={{
                    duration: 0.35,
                    ease: 'easeOut'
                  }} className="text-7xl md:text-8xl font-bold text-caramel tracking-tighter inline-block" data-id="element-232">
                      {formatPrice(total)}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <span className="text-xl font-bold text-caramel mt-auto mb-3" data-id="element-233">
                  USD
                </span>
              </div>
            </div>

            {/* Small trust badge */}
            <div className="flex items-center justify-center gap-1.5 text-xs text-espresso-light mb-8 -mt-4" data-id="element-234">
              <Lock className="w-3.5 h-3.5 text-green-600" data-id="element-235" />
              <span className="font-medium" data-id="element-236">
                Pago 100% seguro · SSL encriptado
              </span>
            </div>

            <div className="max-w-md mx-auto space-y-4 mb-10 text-left" data-id="element-237">
              {['Manual completo en PDF (Descarga inmediata)', 'Recetas exactas y probadas', 'Garantía de 30 días', 'Acceso de por vida'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-238">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0" data-id="element-239">
                    <Check className="w-4 h-4 text-green-600" data-id="element-240" />
                  </div>
                  <span className="text-espresso font-medium" data-id="element-241">{item}</span>
                </div>)}
            </div>

            {/* Main CTA — dynamic price + URL */}
            <a id="cta-final" href={checkoutUrl} onClick={handleCheckoutInterceptor} className="group relative inline-flex items-center justify-center gap-2 w-full md:w-auto px-10 py-5 bg-caramel text-white font-bold text-xl rounded-2xl overflow-hidden transition-all hover:bg-caramel-dark hover:shadow-glow hover:-translate-y-1 scroll-mt-24" data-id="element-242">
              <span className="absolute inset-0 w-full h-full bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-243"></span>
              <span className="relative flex items-center gap-2" data-id="element-244">
                SÍ, QUIERO EMPEZAR POR
                <AnimatePresence mode="popLayout" data-id="element-245">
                  <motion.span key={total.toFixed(2)} initial={{
                  y: 15,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} exit={{
                  y: -15,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="inline-block" data-id="element-246">
                    ${formatPrice(total)}
                  </motion.span>
                </AnimatePresence>
              </span>
              <ArrowRight className="relative w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-247" />
            </a>

            {/* Payment card logos */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5" data-id="element-248">
              {[{
              label: 'Visa',
              src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/visa.svg'
            }, {
              label: 'Mastercard',
              src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/mastercard.svg'
            }, {
              label: 'American Express',
              src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/amex.svg'
            }, {
              label: 'Discover',
              src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/discover.svg'
            }, {
              label: 'Diners Club',
              src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/diners.svg'
            }, {
              label: 'Elo',
              src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/elo.svg'
            }, {
              label: 'UnionPay',
              src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/unionpay.svg'
            }].map(card => <img key={card.label} src={card.src} alt={card.label} width={42} height={28} loading="lazy" decoding="async" className="h-7 w-auto rounded-md shadow-sm border border-black/5" data-id="element-249" />)}
              <span aria-label="UCB" className="flex h-7 items-center justify-center rounded-md border border-black/5 bg-espresso px-2 text-[11px] font-bold tracking-wide text-white shadow-sm" data-id="element-250">
                UCB
              </span>
            </div>

            <p className="mt-4 text-sm text-espresso-light font-medium" data-id="element-251">
              Acceso inmediato tras el pago. Envío digital a todo el mundo.
            </p>

            {/* ORDER BUMPS */}
            <div className="mt-10 pt-10 border-t-2 border-dashed border-caramel/30" data-id="element-252">
              <p className="text-xs font-bold uppercase tracking-widest text-caramel mb-2" data-id="element-253">
                ⚡ Mejora tu pedido
              </p>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-espresso mb-6" data-id="element-254">
                Agrega estos extras a precio especial
              </h3>

              <div className="space-y-4" data-id="element-255">
                <OrderBumpCard checked={bump1} onToggle={() => setBump1(v => !v)} image="https://cdn.magicpatterns.com/uploads/haJqCHN4fzJaX5BJ3qxZgH/Guia_de_Costos_y_Precios_No_pierdas_ni_un_centavo_al_vender.webp" title="Guía de Costos y Precios — No pierdas ni un centavo al vender" description="¿Cuánto cobras por una milhoja? Si dudaste aunque sea un segundo, esta guía es para ti. El 80% de las reposteras cobra de menos sin saberlo — y trabaja más para ganar menos. Con esta fórmula sabes exactamente cuánto cobrar por cada pieza: ingredientes, tiempo, empaque y ganancia real. Una sola venta bien cobrada recupera lo que pagas hoy." price={BUMP_1_PRICE} oldPrice={12.0} data-id="element-256" />
                <OrderBumpCard checked={bump2} onToggle={() => setBump2(v => !v)} image="https://cdn.magicpatterns.com/uploads/5sHCiMZA5sF8JW8HWn8xan/Pack_de_10_Rellenos_Gourmet_para_Milhojas.webp" title="Pack de 10 Rellenos Gourmet para Milhojas" description="Las recetas básicas las vende cualquiera. Estas no. Pistacho Royale, Coulis de Frutos Rojos, Ganache de Chocolate Belga y 7 más. Son los rellenos que más se viralizan en Instagram y los que más pedidos generan por WhatsApp. Las reposteras que los agregan a su menú reportan hasta 3 veces más pedidos por semana. Solo disponible aquí. No los encontrarás en el manual." price={BUMP_2_PRICE} oldPrice={15.0} data-id="element-257" />
              </div>

              {/* Cart Summary */}
              <div className="mt-8 bg-cream-dark/30 border-2 border-caramel/20 rounded-2xl p-6 text-left" data-id="element-258">
                <div className="flex items-center gap-2 mb-4" data-id="element-259">
                  <ShoppingBag className="w-5 h-5 text-caramel" data-id="element-260" />
                  <h4 className="font-bold text-espresso text-lg" data-id="element-261">
                    Resumen de tu compra
                  </h4>
                </div>

                <div className="space-y-3" data-id="element-262">
                  <CartLine label="Master en Repostería en Casa" price={BASE_PRICE} data-id="element-263" />

                  <AnimatePresence initial={false} data-id="element-264">
                    {bump1 && <motion.div key="bump1" initial={{
                    opacity: 0,
                    height: 0,
                    marginTop: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto',
                    marginTop: 12
                  }} exit={{
                    opacity: 0,
                    height: 0,
                    marginTop: 0
                  }} transition={{
                    duration: 0.25
                  }} style={{
                    overflow: 'hidden'
                  }} data-id="element-265">
                        <CartLine label="Guía de Costos y Precios" price={BUMP_1_PRICE} isBump data-id="element-266" />
                      </motion.div>}
                    {bump2 && <motion.div key="bump2" initial={{
                    opacity: 0,
                    height: 0,
                    marginTop: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto',
                    marginTop: 12
                  }} exit={{
                    opacity: 0,
                    height: 0,
                    marginTop: 0
                  }} transition={{
                    duration: 0.25
                  }} style={{
                    overflow: 'hidden'
                  }} data-id="element-267">
                        <CartLine label="Pack de 10 Rellenos Gourmet" price={BUMP_2_PRICE} isBump data-id="element-268" />
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div className="mt-4 pt-4 border-t border-caramel/20 flex items-center justify-between" data-id="element-269">
                  <span className="font-bold text-espresso text-lg" data-id="element-270">Total</span>
                  <AnimatePresence mode="popLayout" data-id="element-271">
                    <motion.span key={total.toFixed(2)} initial={{
                    y: 15,
                    opacity: 0
                  }} animate={{
                    y: 0,
                    opacity: 1
                  }} exit={{
                    y: -15,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} className="font-bold text-caramel text-2xl" data-id="element-272">
                      ${formatPrice(total)} USD
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>

              {/* Secondary CTA after summary for convenience */}
              <a href={checkoutUrl} onClick={handleCheckoutInterceptor} className="group relative inline-flex items-center justify-center gap-2 w-full mt-6 px-8 py-4 bg-espresso text-white font-bold text-lg rounded-2xl overflow-hidden transition-all hover:bg-espresso-light hover:-translate-y-0.5" data-id="element-273">
                <span className="relative flex items-center gap-2" data-id="element-274">
                  FINALIZAR COMPRA POR
                  <AnimatePresence mode="popLayout" data-id="element-275">
                    <motion.span key={total.toFixed(2)} initial={{
                    y: 12,
                    opacity: 0
                  }} animate={{
                    y: 0,
                    opacity: 1
                  }} exit={{
                    y: -12,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} className="inline-block" data-id="element-276">
                      ${formatPrice(total)}
                    </motion.span>
                  </AnimatePresence>
                </span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-277" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Upsell Popup */}
      <UpsellPopup open={showUpsell} onClose={() => setShowUpsell(false)} onAccept={handleUpsellAccept} onDecline={handleUpsellDecline} trueTotal={TRUE_TOTAL} bundlePrice={BUNDLE_PRICE} savings={SAVINGS} data-id="element-278" />
    </section>;
}
type OrderBumpCardProps = {
  checked: boolean;
  onToggle: () => void;
  image: string;
  title: string;
  description: string;
  price: number;
  oldPrice: number;
};
function OrderBumpCard({
  checked,
  onToggle,
  image,
  title,
  description,
  price,
  oldPrice
}: OrderBumpCardProps) {
  return <button type="button" onClick={onToggle} className={`relative w-full text-left rounded-2xl border-2 transition-all duration-300 group overflow-hidden ${checked ? 'bg-amber-50 border-caramel shadow-glow' : 'bg-white border-dashed border-caramel/40 hover:border-caramel hover:bg-amber-50/50'}`} data-id="element-279">
      {/* Flash effect — only when unchecked */}
      {!checked && <>
          <span className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-caramel/40 animate-[bumpPulse_2.4s_ease-in-out_infinite] z-30" data-id="element-280" />
          <span className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden z-30" data-id="element-281">
            <span className="absolute -inset-y-2 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-caramel/20 to-transparent skew-x-[-20deg] animate-[bumpFlash_3s_ease-in-out_infinite]" data-id="element-282" />
          </span>
        </>}

      {/* Image banner on top */}
      <div className="relative w-full aspect-[16/10] sm:aspect-[2/1] bg-cream-dark/30 overflow-hidden" data-id="element-283">
        <img src={image} alt={title} width={1024} height={640} className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-284" />
        {/* Floating price badge */}
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur px-3 py-2 rounded-xl shadow-lg border border-caramel/20" data-id="element-285">
          <div className="text-[10px] text-espresso-light/60 line-through font-medium leading-none mb-0.5" data-id="element-286">
            ${oldPrice.toFixed(2)}
          </div>
          <div className="text-lg md:text-xl font-bold text-caramel leading-none" data-id="element-287">
            +${price.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Content stacked below */}
      <div className="relative p-5 md:p-6" data-id="element-288">
        <div className="flex items-start gap-3" data-id="element-289">
          <div className={`shrink-0 w-7 h-7 rounded-md border-2 flex items-center justify-center mt-0.5 transition-all ${checked ? 'bg-caramel border-caramel' : 'bg-white border-caramel/50 group-hover:border-caramel'}`} data-id="element-290">
            {checked ? <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-291" /> : <Plus className="w-4 h-4 text-caramel" strokeWidth={3} data-id="element-292" />}
          </div>

          <div className="flex-1 min-w-0" data-id="element-293">
            <h4 className="font-bold text-espresso text-base md:text-lg leading-snug mb-2" data-id="element-294">
              ¡SÍ! Agregar: {title}
            </h4>
            <p className="text-sm text-espresso-light leading-relaxed" data-id="element-295">
              {description}
            </p>
          </div>
        </div>
      </div>
    </button>;
}
type CartLineProps = {
  label: string;
  price: number;
  isBump?: boolean;
};
function CartLine({
  label,
  price,
  isBump
}: CartLineProps) {
  return <div className="flex items-center justify-between gap-3" data-id="element-296">
      <div className="flex items-center gap-2 min-w-0" data-id="element-297">
        {isBump ? <Plus className="w-4 h-4 text-caramel shrink-0" strokeWidth={3} data-id="element-298" /> : <Check className="w-4 h-4 text-green-600 shrink-0" strokeWidth={3} data-id="element-299" />}
        <span className="text-espresso text-sm md:text-base font-medium truncate" data-id="element-300">
          {label}
        </span>
      </div>
      <span className="text-espresso font-bold text-sm md:text-base shrink-0" data-id="element-301">
        ${price.toFixed(2)}
      </span>
    </div>;
}